import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import Alert from 'react-bootstrap/Alert';
import Button from '@components/smartAdminUI/Button';
import useInterval from '@hooks/useInterval';

const RowLockMessage = ({
  rowLockEntity,
  message,
  lockedBy,
  lockedByMeWithinSession,
  impersonatedBy,
  userName,
  isAdmin,
  checkStatus = () => null,
  onDiscard = () => null,
  displayActions = true,
  lastUpdatedOn = null,
  lastCheckedOn = null,
}) => {
  const rowLockTimer = 5; // minutes
  const [minutesElapsed, setMinutesElapsed] = useState(null);
  const [secondsElapsed, setSecondsElapsed] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(null);
  const [callback, setCallback] = useState(null);
  const [holdCallbackFiring, setHoldCallbackFiring] = useState(false);
  const entityType = useMemo(() => `${rowLockEntity?.type[0].toUpperCase()}${rowLockEntity?.type.slice(1)}`, [rowLockEntity]);
  const canTakeAction = useMemo(() => minutesElapsed >= rowLockTimer, [minutesElapsed]);
  const timeElapsedMessage = useMemo(() => {
    if (minutesElapsed == null) return null;
    if (minutesElapsed < 1 && secondsElapsed < 60) return `${secondsElapsed}s ago`;
    if (minutesElapsed >= 60) {
      const hours = Math.floor(minutesElapsed / 60);
      const minutes = minutesElapsed % 60;
      return minutes > 0
        ? `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes > 1 ? 's' : ''} ago`
        : `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    return `${minutesElapsed} minute${minutesElapsed > 1 ? 's' : ''} ago`;
  }, [minutesElapsed, secondsElapsed]);

  const lastUpdate = useMemo(() => lastUpdatedOn && new Date(lastUpdatedOn), [lastUpdatedOn]);
  const timeRemainingMessage = useMemo(() => {
    const diffInMilliseconds = Date.now() - new Date(lastUpdate).getTime();
    const differenceInSeconds = Math.floor(diffInMilliseconds / 1000);
    setSecondsElapsed(differenceInSeconds);
    const remainingTimeInSeconds = (rowLockTimer * 60) - Math.floor(differenceInSeconds);
    if (remainingTimeInSeconds <= 0) return '00:00:00';

    const remainingMinutes = Math.floor(remainingTimeInSeconds / 60);
    const remainingSeconds = remainingTimeInSeconds % 60;

    // Format minutes and seconds to always have two digits
    const formattedMinutes = String(remainingMinutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');

    return `00:${formattedMinutes}:${formattedSeconds}`;
  }, [secondsRemaining]);

  const updateElapsedTime = useCallback(() => {
    if (!lastUpdate) return;
    const minutes = Math.floor((Math.abs(Date.now() - lastUpdate) / 1000) / 60);
    setMinutesElapsed(minutes);
  }, [lastUpdate]);

  const handleDiscard = () => {
    checkStatus();
    setCallback(() => onDiscard);
    setHoldCallbackFiring(true); // put the callback firing on hold until the status check is complete
  };

  useEffect(() => {
    if (callback && holdCallbackFiring) setHoldCallbackFiring(false);
  }, [lastCheckedOn]);

  useEffect(() => {
    if (callback && !holdCallbackFiring && (isAdmin || canTakeAction || lockedByMeWithinSession)) {
      callback();
      setCallback(null);
    }
  }, [holdCallbackFiring]);

  useEffect(updateElapsedTime, [lastUpdate]);

  useInterval(() => {
    if (minutesElapsed < rowLockTimer) setSecondsRemaining(secondsRemaining - 1);
    updateElapsedTime();
  }, 1000);

  return (
    <div className="row">
      <div className="col">
        <Alert variant="danger fade show">
          <h3>
            <i className="far fa-user-lock mr-2" />
            {message
              || ((userName === lockedBy) && (userName === impersonatedBy) && `This ${entityType} is locked by you`)
              || ((userName === impersonatedBy) && (userName !== lockedBy) && `This ${entityType} is locked by you, logged in as ${lockedBy}`)
              || ((userName === lockedBy) && (userName !== impersonatedBy) && `This ${entityType} is locked by ${impersonatedBy}, logged in as you`)
              || ((userName !== lockedBy) && (userName !== impersonatedBy) && (lockedBy !== impersonatedBy) && `This ${entityType} is locked by ${impersonatedBy}, logged in as ${lockedBy}`)
              || ((userName !== lockedBy) && (userName !== impersonatedBy) && (lockedBy === impersonatedBy) && `This ${entityType} is locked by ${impersonatedBy}`)}
          </h3>
          {!lockedByMeWithinSession && (userName === lockedBy) && (userName === impersonatedBy) && <p>It may either be open in another browser window/tab or possibly on another device.</p>}
          {lastUpdate && timeElapsedMessage && (
            <p>
              {`The lock was last updated on ${new Intl.DateTimeFormat(undefined, { dateStyle: 'full', timeStyle: 'long', timeZone: undefined }).format(lastUpdate)} (${timeElapsedMessage}).`}
            </p>
          )}
          {!isAdmin && !lockedByMeWithinSession && !canTakeAction && (
            <>
              <p>{`You will be able to remove the lock on this ${entityType} record in ${timeRemainingMessage}.`}</p>
            </>
          )}
          {(isAdmin || lockedByMeWithinSession || (displayActions && canTakeAction)) && (
            <>
              <p>{`You may remove the lock on this ${entityType} record by clicking on the button below:`}</p>
              <Button name="btnRemoveLock" variant="primary" onClick={handleDiscard}>
                <i className="fas fa-lock-open mr-2" aria-hidden="true" />
                Remove Lock
              </Button>
            </>
          )}
        </Alert>
      </div>
    </div>
  );
};

RowLockMessage.propTypes = {
  rowLockEntity: PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.number,
  }),
  message: PropTypes.string,
  lockedBy: PropTypes.string,
  lockedByMeWithinSession: PropTypes.bool,
  impersonatedBy: PropTypes.string,
  userName: PropTypes.string,
  isAdmin: PropTypes.bool,
  checkStatus: PropTypes.func,
  onDiscard: PropTypes.func,
  displayActions: PropTypes.bool,
  lastUpdatedOn: PropTypes.string,
  lastCheckedOn: PropTypes.number,
};

export default RowLockMessage;
